import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Icon,
  Panel,
  Stat,
  StatusDot,
  Tag,
  useErrorDialog,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { RESIDENCY_COUNTRIES_KEY, RESIDENCY_MODE_KEY } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { EditSettingDialog } from './EditSettingDialog.tsx';
import { ResidencyPolicy } from './residency/ResidencyPolicy.tsx';

/**
 * Platform management — global configuration, and the log that makes it
 * accountable.
 *
 * The organising rule of this screen is the first one in the spec: "every
 * parameter change is versioned with actor, before/after and effective time in
 * the configuration log." So the parameters table and the log are not two
 * features, they are one — a value on this screen is only trustworthy because
 * the row beneath it says who moved it and when it took effect.
 */

/* ── Wire shapes ───────────────────────────────────────────────────────────
   snake_case, straight off the SELECT lists in
   services/api/src/modules/backoffice/platform.ts. Nothing is renamed on the
   way in: a field that reads differently here than in the SQL is a field
   somebody will eventually mis-map. */

interface SettingRow {
  key: string;
  value: string;
  /** Only these may carry a brand override; the rest cascade unconditionally. */
  per_brand: boolean;
  description: string | null;
  updated_at: string;
  /** NULL brand_id => the global row. A name here means it is a brand override. */
  brand: string | null;
  /** The id BEHIND that name. Editing an override has to address it. */
  brand_id: string | null;
}

interface HistoryRow {
  id: string;
  setting_key: string;
  value_before: string | null;
  value_after: string;
  effective_at: string;
  /** LEFT JOIN on identity.admin_user — null when changed_by is not an admin id. */
  actor: string | null;
  brand: string | null;
}

interface BrandRow {
  id: string;
  ref: string;
  name: string;
  domain: string;
  licence_ref: string | null;
  licence_expires_at: string | null;
  active: boolean;
  /** Counts and booleans are cast ::text in SQL to survive the pg driver. */
  players: string;
  licence_expiring: string;
  /**
   * Whether the licence has ALREADY LAPSED, answered by the server.
   *
   * This row type omitted it, so this screen had only two states — "expiring"
   * and "in date" — and rendered a licence that expired last month as
   * "Expiring within 60 days". The brands screen reads the same endpoint and
   * has always carried it; this one simply never asked for the field.
   */
  licence_expired: string;
}

/* ── Time ──────────────────────────────────────────────────────────────────
   "Timezone is display-only — all storage is UTC." Every timestamp below is an
   ISO instant that we render in PHT; nothing on this screen writes a local
   time back. */
const DISPLAY_TZ = 'Asia/Manila';

const dayTimeFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: DISPLAY_TZ,
});
const dayFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: DISPLAY_TZ,
});

function dayTime(iso: string | null): string {
  return iso ? dayTimeFmt.format(new Date(iso)).replace(',', '') : '—';
}

function day(iso: string | null): string {
  return iso ? dayFmt.format(new Date(iso)) : '—';
}

function daysUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/* ── Scope ─────────────────────────────────────────────────────────────────
   The design shows a Scope column reading "Global", "All brands" and "Back
   office". platform.setting has no scope column — scope is only DERIVABLE from
   brand_id plus per_brand — so this derives the two states the data can
   actually prove and the notice above the table owns the rest. Printing "Back
   office" here would be a label the database cannot support. */
/**
 * What a setting the design shows but the database does not hold renders as.
 * Rule of the house: never a plausible-looking default. A blank here would read
 * as "unset"; this reads as "there is no such row", which is the truth.
 *
 * The row that is absent is `platform.setting` keyed on the parameter named,
 * and that name belongs here rather than in the sentence. An operator who read
 * "no platform.name row in platform.setting" could do nothing with the second
 * half of it: it is the identifier the schema happens to use, not something
 * they can look up, ask for or repeat, and the only part they need is that the
 * platform holds no such setting at all.
 *
 * The parameter key went too, on 30 Oct (walk D2). It was kept on the reasoning
 * that the Parameters column prints keys, so an operator could match the line
 * against a row — but a missing setting HAS no row to match, so "no
 * platform.name setting is recorded" named a thing nowhere on the screen. The
 * key sits beside each call instead, and the row's own label ("Platform name")
 * already says which setting is meant.
 */
function missing() {
  return <span className="text-subtle">no such setting is recorded</span>;
}

/**
 * What a value renders as when the read that would have carried it FAILED.
 *
 * Deliberately not missing(): "there is no such row" is a claim about the
 * database, and making it about a request that never came back would be
 * asserting something nobody checked. An operator who reads "no platform.name
 * row" decides the platform is unconfigured; the truth was that we could not
 * ask.
 */
function unread(source: string) {
  return <span className="text-subtle">not read — the {source} request failed</span>;
}

function scopeOf(s: SettingRow): { label: string; tone: TagTone } {
  if (s.brand) return { label: `${s.brand} override`, tone: 'outline' };
  return s.per_brand
    ? { label: 'Global · override allowed', tone: 'accent' }
    : { label: 'Global · all brands', tone: 'neutral' };
}

const parameterColumns: Column<SettingRow>[] = [
  {
    key: 'key',
    header: 'Parameter',
    render: (s) => (
      <>
        <span className="tabular">{s.key}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {s.description ?? 'No description recorded'}
        </div>
      </>
    ),
  },
  {
    key: 'value',
    header: 'Value',
    // Printed exactly as stored. platform.setting is `text` with no type or
    // unit metadata, so agent.early_payout_fee reads 25000 — deciding here that
    // it means ₱250.00 would be inventing a unit the API never sent.
    render: (s) => <span className="tabular">{s.value}</span>,
  },
  {
    key: 'scope',
    header: 'Scope',
    render: (s) => {
      const scope = scopeOf(s);
      return <Tag tone={scope.tone}>{scope.label}</Tag>;
    },
  },
  {
    key: 'updated',
    header: 'Last changed',
    secondary: true,
    render: (s) => <span className="tabular">{day(s.updated_at)}</span>,
  },
];

/**
 * The columns, plus the Edit control — a FACTORY, because the control depends
 * on the operator.
 *
 * Edit used to be permanently disabled with the aria-label "no write route in
 * this build", and that was the honest thing to say: `platform.setting` had no
 * writer at runtime and an Edit that only fired a "saved" toast is worse than
 * no Edit at all. `POST /api/platform/settings` now exists, so the claim has
 * changed and every place that made it has changed with it.
 *
 * It is still DISABLED for an operator who does not hold `platform.manage`,
 * with the true reason on it — an Auditor or a Finance Lead reads this screen
 * legitimately (that is what `platform.view` is for) and needs to know the
 * value can be changed and by whom, rather than meeting a control that is
 * simply absent.
 */
/**
 * The two parameters this table may NOT edit, and the reason it says so rather
 * than offering a control whose only outcome is a refusal.
 *
 * They are the residency policy — a mode and the country list that mode reads —
 * and they only mean anything together. `POST /api/platform/settings` refuses
 * them outright: a mode moved while the list still says something else is a
 * licence control that reports one rule and applies another, and no amount of
 * validating one row at a time closes that. The Player residency section below
 * writes both at once, against the real country list.
 */
const OWNED_BY_RESIDENCY = new Set<string>([RESIDENCY_MODE_KEY, RESIDENCY_COUNTRIES_KEY]);

/**
 * Parameters nothing reads any more.
 *
 * `points.per_peso_wagered` was the points rate until sql/460. It is a decimal
 * string — the only way to use it was to parse it into a float — and the live
 * rate is now a pair of integers per brand in `points.earning_rule`, edited on
 * the Points screen.
 *
 * The row cannot simply be deleted: `dayon_app` holds no DELETE anywhere, and
 * the seeded rules record it as their provenance. So it stays, and the question
 * is what the screen does with it.
 *
 * A KNOB THAT TURNS AND CHANGES NOTHING IS WORSE THAN ONE THAT WAS REMOVED. An
 * operator who moves this expects the award job to pay differently tomorrow; it
 * will not, and nothing on the screen would have told them. The write route
 * would have accepted it, the configuration log would have recorded a change
 * that had no effect, and the next person reading that log would draw the wrong
 * conclusion about why a figure moved.
 *
 * Disabled with the reason, using the same mechanism as the residency pair
 * rather than a second one. The value stays visible, because it is still the
 * provenance of the rules that superseded it.
 */
const SUPERSEDED = new Set<string>(['points.per_peso_wagered']);

function parameterColumnsFor(
  onEdit: (s: SettingRow) => void,
  mayManage: boolean,
): Column<SettingRow>[] {
  return [
    ...parameterColumns,
    {
      key: 'action',
      header: 'Action',
      numeric: true,
      render: (s) => (
        <Button
          small
          disabled={!mayManage || OWNED_BY_RESIDENCY.has(s.key) || SUPERSEDED.has(s.key)}
          aria-label={
            SUPERSEDED.has(s.key)
              ? `Edit ${s.key} — this parameter is no longer read by anything; the live points rate is set per brand on the Points screen`
              : OWNED_BY_RESIDENCY.has(s.key)
                ? `Edit ${s.key} — this parameter is half of the residency policy and is set in the Player residency section below, where the mode and the country list are written together`
                : mayManage
                ? `Edit ${s.key}`
                : `Edit ${s.key} — you do not hold platform.manage, which changing a parameter requires`
          }
          onClick={(e) => {
            /* The row itself opens the read drawer. Without this the Edit
               click would open both, and the dialog would be stacked over a
               drawer nobody asked for. */
            e.stopPropagation();
            onEdit(s);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];
}

const historyColumns: Column<HistoryRow>[] = [
  {
    key: 'effective',
    header: 'Effective',
    render: (h) => <span className="tabular">{dayTime(h.effective_at)}</span>,
  },
  {
    key: 'actor',
    header: 'Actor',
    // setting_history.changed_by is bare text, not an FK, so the join to
    // identity.admin_user can miss. An unresolved actor is named as such
    // instead of being quietly blanked — an unattributed config change is
    // exactly the row an auditor wants to see.
    render: (h) => h.actor ?? <span className="text-subtle">unattributed</span>,
  },
  {
    key: 'parameter',
    header: 'Parameter',
    render: (h) => <span className="tabular">{h.setting_key}</span>,
  },
  {
    key: 'change',
    header: 'Before → after',
    render: (h) => (
      <span className="tabular">
        {h.value_before ?? '—'} → {h.value_after}
      </span>
    ),
  },
  {
    key: 'scope',
    header: 'Scope',
    secondary: true,
    render: (h) => (
      <Tag tone={h.brand ? 'outline' : 'neutral'}>{h.brand ?? 'Global'}</Tag>
    ),
  },
];

const brandColumns: Column<BrandRow>[] = [
  {
    key: 'brand',
    header: 'Brand',
    render: (b) => (
      <>
        {b.name}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {b.ref} · {b.domain}
        </div>
      </>
    ),
  },
  {
    key: 'players',
    header: 'Players',
    numeric: true,
    secondary: true,
    render: (b) => <span className="tabular">{formatCount(Number(b.players))}</span>,
  },
  {
    key: 'licence',
    header: 'Licence',
    render: (b) =>
      b.licence_ref ?? <span className="text-subtle">none recorded</span>,
  },
  {
    key: 'expires',
    header: 'Licence expires',
    // "Licence expiry drives a 60-day advance warning on the dashboard." The
    // API computes that window in SQL; the dot never carries it alone.
    render: (b) => {
      if (!b.licence_expires_at) return <span className="text-subtle">no expiry recorded</span>;
      /* THREE STATES, NOT TWO. `licence_expiring` is the 60-day window and says
         nothing about a licence that has already gone; without `licence_expired`
         this cell rendered a lapsed licence as "Expiring within 60 days", which
         reads as a deadline approaching rather than one missed. Both booleans
         come from the server so this screen and the dashboard cannot drift. */
      const expired = b.licence_expired === 'true';
      const soon = b.licence_expiring === 'true';
      return (
        <>
          <StatusDot
            /* `attention` for both, because StatusDot has no critical tone —
               the brands screen resolves the same way. The WORD carries the
               severity; the dot only says "not fine". */
            tone={expired || soon ? 'attention' : 'ok'}
            label={expired ? 'EXPIRED' : soon ? 'Expiring within 60 days' : 'In date'}
          />{' '}
          <span className="tabular">{day(b.licence_expires_at)}</span>
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {expired
              ? 'this licence has lapsed — the brand is operating without a current one'
              : soon
                ? 'renew — inside the 60-day window'
                : `in ${daysUntil(b.licence_expires_at)} days`}
          </div>
        </>
      );
    },
  },
  {
    key: 'active',
    header: 'Status',
    render: (b) => <Tag tone={b.active ? 'accent' : 'quiet'}>{b.active ? 'Live' : 'Suspended'}</Tag>,
  },
];

export function PlatformSettings() {
  const { brandId, brands: sessionBrands, allowed } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canReadPlatform = allowed('platform.view');
  const { showError } = useErrorDialog();

  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [brandsLoading, setBrandsLoading] = useState(true);
  /*
   * Whether each read FAILED, as distinct from succeeding and returning
   * nothing.
   *
   * The error modal is dismissed in a second, and what was left standing behind
   * it was copy written for a genuinely empty database — "platform.setting
   * returned no row … this is a real empty read, not a filter" over a
   * Parameters tile reading 0. That sentence is false in exactly the case it is
   * most likely to be read. "There is no such parameter" and "we could not ask
   * what the parameters are" are different facts, and on a money platform
   * somebody will act on the wrong one.
   *
   * Three flags because three independent GETs back this screen and any one can
   * fail alone: a dead history route must not put "could not be read" over a
   * parameters table that arrived intact.
   */
  const [settingsFailed, setSettingsFailed] = useState(false);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [brandsFailed, setBrandsFailed] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selected, setSelected] = useState<SettingRow | null>(null);
  /** The parameter whose value is being changed, or null. */
  const [editing, setEditing] = useState<SettingRow | null>(null);
  /*
   * Bumped after every accepted write, so all three reads run again.
   *
   * The value, its `updated_at`, the derived scope tag and the configuration
   * log all move together on one write, and patching the row in place would
   * leave the log — the thing that makes the value trustworthy — showing the
   * board as it was before the change the operator just made.
   */
  const [writeCount, setWriteCount] = useState(0);

  /* All three routes are global by construction — none of them calls
     brandScope(), so no brandId is sent. The brand selector is honoured
     client-side below instead of being passed as a query the API would silently
     ignore.

     Three chains rather than one Promise.all: the three panels below are backed
     by three separate reads, and a single shared catch could only report that
     "something" did not load — a verdict that would then have to be printed
     over the two panels that did. */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPlatform) {
      setSettingsLoading(false);
      setHistoryLoading(false);
      setBrandsLoading(false);
      return;
    }
    let cancelled = false;
    setSettingsLoading(true);
    setHistoryLoading(true);
    setBrandsLoading(true);

    api
      .get<{ items: SettingRow[] }>('/api/platform/settings')
      .then((s) => {
        if (cancelled) return;
        setSettings(s.items);
        setSettingsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Cleared on purpose: a previous list of parameters sitting under a
        // failure notice would be read as the current configuration.
        setSettings([]);
        setSettingsFailed(true);
        showError({
          title: 'Could not load platform configuration',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setSettingsLoading(false));

    api
      .get<{ items: HistoryRow[] }>('/api/platform/settings/history')
      .then((h) => {
        if (cancelled) return;
        setHistory(h.items);
        setHistoryFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setHistory([]);
        setHistoryFailed(true);
        showError({
          title: 'Could not load the configuration log',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setHistoryLoading(false));

    api
      .get<{ items: BrandRow[] }>('/api/platform/brands')
      .then((b) => {
        if (cancelled) return;
        setBrands(b.items);
        setBrandsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setBrands([]);
        setBrandsFailed(true);
        showError({
          title: 'Could not load brands and licences',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setBrandsLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadPlatform, showError, writeCount]);

  const scopedBrandName = useMemo(
    () => sessionBrands.find((b) => b.id === brandId)?.name ?? null,
    [sessionBrands, brandId],
  );

  // Global rows stay visible under a brand scope because they cascade — hiding
  // them would tell an operator the brand has no currency or no idle timeout.
  const visibleSettings = useMemo(
    () =>
      scopedBrandName
        ? settings.filter((s) => s.brand === null || s.brand === scopedBrandName)
        : settings,
    [settings, scopedBrandName],
  );

  const visibleHistory = useMemo(
    () =>
      scopedBrandName
        ? history.filter((h) => h.brand === null || h.brand === scopedBrandName)
        : history,
    [history, scopedBrandName],
  );

  /*
   * platform.view, not access.manage.
   *
   * Nothing on this screen writes — it is the parameters, their version history
   * and the brand licences, all read. Gating it on access.manage made reading a
   * parameter cost the power to rewrite the permission matrix, and since only
   * Super Admin holds that key, the Finance Lead could not read the thresholds
   * behind their queue and the Auditor could not read the configuration log it
   * exists to audit. The three gates — sidebar, this guard and every GET on
   * services/api/src/modules/backoffice/platform.ts — all name platform.view.
   */
  if (!canReadPlatform) {
    return (
      <EmptyState icon="lock-key" title="Platform management">
        This module needs the platform.view capability. Your role does not hold it. Reading a
        parameter and changing one are separate keys — the write is platform.manage — and you hold
        neither.
      </EmptyState>
    );
  }

  /* One per read: a skeleton over the brands table while the parameters route
     is still in flight would claim the brands are still coming when they have
     already arrived. */
  const settingsBusy = settingsLoading && settings.length === 0;
  const historyBusy = historyLoading && history.length === 0;
  const brandsBusy = brandsLoading && brands.length === 0;
  /** The global row for a key. Brand overrides are read from the table, not here. */
  const settingValue = (key: string): string | null =>
    settings.find((s) => s.key === key && s.brand === null)?.value ?? null;

  const perBrandCount = visibleSettings.filter((s) => s.per_brand).length;
  const activeBrands = brands.filter((b) => b.active);
  const expiringLicences = brands.filter((b) => b.licence_expiring === 'true');
  const nextLicence = brands
    .filter((b) => b.licence_expires_at)
    .toSorted((a, b) => a.licence_expires_at!.localeCompare(b.licence_expires_at!))[0];
  const lastChange = visibleHistory[0];
  const configuredTz = settingValue('platform.timezone');
  const currency = settingValue('platform.default_currency');

  // Order and labels are the design's "General settings" card, verbatim. Two of
  // its six rows are not backed by anything, and say so rather than reprinting
  // the prototype's mock copy.
  const generalRows: { label: string; value: ReactNode }[] = [
    {
      label: 'Platform name',
      value: settingsFailed
        ? unread('parameter')
        : (settingValue('platform.name') ?? missing()),
    },
    {
      label: 'Brands live',
      value: brandsFailed ? (
        unread('brand')
      ) : activeBrands.length > 0 ? (
        activeBrands.map((b) => b.name).join(' · ')
      ) : (
        <span className="text-subtle">no active brand</span>
      ),
    },
    {
      label: 'Default currency',
      value: settingsFailed
        ? unread('parameter')
        : currency
          ? currency === 'PHP'
            ? 'PHP (₱)'
            : currency
          : missing(),
    },
    {
      label: 'Timezone',
      value: settingsFailed
        ? unread('parameter')
        : configuredTz
          ? `${configuredTz} — display only, storage is UTC`
          : missing(),
    },
    {
      label: 'Locales',
      value: settingsFailed
        ? unread('parameter')
        : (settingValue('platform.locales') ?? missing()),
    },
    {
      label: 'Licence',
      value: brandsFailed ? (
        unread('brand')
      ) : nextLicence ? (
        `${nextLicence.licence_ref ?? 'unreferenced'} · first expiry ${day(nextLicence.licence_expires_at)}`
      ) : (
        <span className="text-subtle">no licence recorded on any brand</span>
      ),
    },
  ];

  return (
    <>
      {/*
        THE HEADING KEEPS THE NAME THE SCREEN ALREADY HAD, THOUGH THE SIDEBAR
        SAYS SOMETHING NARROWER.

        The sidebar calls this module "Platform settings". The h3 this replaces
        said "Platform management", and it stays that way because the screen is
        wider than its sidebar entry: under the parameters sit the player
        residency policy, the configuration log, and every brand with its
        licence expiry — and that last one is deliberately NOT narrowed by the
        brand scope, because a lapsing licence is a platform obligation. An
        operator sent here to start a renewal, reading a heading that said only
        "settings", would decide they were on the wrong screen, and there is no
        other screen holding the whole set. The capability refusal above names
        it "Platform management" for the same reason, and so the two names stay
        as they were while the purpose below says both halves out loud.
      */}
      <ScreenHeader
        title="Platform management"
        purpose="The settings every brand runs on, and the record of who changed each one — administrators set them here, auditors come to see what moved and when."
        /*
          The caption kept exactly as it was: both of its tri-state halves, the
          clause about versioning, and the brand scope.

          NEITHER HALF MAY COLLAPSE INTO A ZERO, which is the whole reason it is
          shaped like this. "Loading…" means no answer has arrived yet;
          "parameters not read" and "configuration log not read" mean that
          request failed, so the figure that would have stood there was never
          taken at all; and a count means the platform answered. Turning a
          failed read into "0 parameters" would be this screen telling somebody
          the platform is unconfigured when the truth is that nobody managed to
          ask — and on a screen whose values decide automated outcomes about
          players' money, that is a conclusion people act on.

          The two halves are stated INDEPENDENTLY because they are two separate
          reads and either can fail alone: a dead configuration log must not put
          "could not be read" over a parameters count that arrived intact. It is
          the same reason the three failure flags above are three and not one.

          The versioning clause and the brand-scope suffix stay SEPARATE
          expressions appended to whichever state won, because both are true of
          every state: a failed read still failed inside a scope, and a reader
          who does not know whether they are looking at one brand or at all of
          them cannot judge either figure.
        */
        meta={
          <>
            {settingsBusy || historyBusy
              ? 'Loading…'
              : `${
                  settingsFailed
                    ? 'parameters not read'
                    : `${formatCount(visibleSettings.length)} parameters`
                } · ${
                  historyFailed
                    ? 'configuration log not read'
                    : `${formatCount(visibleHistory.length)} versioned changes`
                }`}
            {' · global configuration, every change versioned'}
            {scopedBrandName ? ` · scoped to ${scopedBrandName}` : ''}
          </>
        }
        /*
          One control, moved across untouched — and still only one.

          THE DESIGN'S PRIMARY CTA IS GONE, and that is deliberate. It used to
          be a permanently disabled "Save changes" carrying the label "no write
          route in this build". There IS a writer now — but it is not a bulk
          save: `POST /api/platform/settings` moves ONE parameter and writes ONE
          configuration-log row for it, because "who changed the payout hold,
          from what, to what" is the question the log exists to answer and a
          batch write cannot answer it. A "Save changes" button over a screen
          with no staged edits would be a control with nothing to do; the Edit on
          each row is the real one.
        */
        actions={
          <Button onClick={() => setHistoryOpen(true)} disabled={historyBusy}>
            <Icon name="clock-counter-clockwise" />
            Configuration log
          </Button>
        }
      />

      {expiringLicences.length > 0 && (
        <div className="notice">
          <Icon name="warning-circle" />
          <div>
            {expiringLicences.length === 1
              ? `${expiringLicences[0]!.name}'s licence expires`
              : `${formatCount(expiringLicences.length)} brand licences expire`}{' '}
            inside the 60-day advance-warning window
            {expiringLicences.length === 1
              ? ` (${day(expiringLicences[0]!.licence_expires_at)})`
              : ''}
            . Renewal is a regulator lead time, not an operational one — start it now.
          </div>
        </div>
      )}

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          {allowed('platform.manage') ? (
            <>
              A parameter is changed one at a time, from the Edit on its row. Each change writes
              its own row to the configuration log below, at the same moment as the value itself,
              carrying your name, the value before and the value after. It takes effect
              immediately: there is no staging, no schedule and no second approver, because{' '}
              a change-request record does not exist and
              configuration does not go through the maker-checker the money paths use.
            </>
          ) : (
            <>
              You can read every parameter and the log of who changed each one —{' '}
              <span className="signin-perm">platform.view</span> — and you cannot change one.
              Editing a parameter requires <span className="signin-perm">platform.manage</span>,
              which is held by Super Admin: these values decide automated outcomes about players&rsquo;
              money, so reading them and moving them are separate capabilities.
            </>
          )}
        </div>
      </div>

      <div className="stat-grid">
        {/* Every tile here is counted off rows. A read that failed has no rows,
            and a tile reading 0 is a claim — so each says which read did not
            come back instead of printing a zero nobody measured. */}
        <Stat
          label="Parameters"
          value={settingsBusy || settingsFailed ? '—' : formatCount(visibleSettings.length)}
          delta={
            settingsFailed
              ? 'not counted — the parameter read failed'
              : settingsBusy
                ? undefined
                : `${formatCount(perBrandCount)} may be overridden per brand`
          }
        />
        <Stat
          label="Brands live"
          value={brandsBusy || brandsFailed ? '—' : formatCount(activeBrands.length)}
          delta={
            brandsFailed
              ? 'not counted — the brand read failed'
              : brandsBusy
                ? undefined
                : activeBrands.map((b) => b.name).join(' · ') || 'none active'
          }
        />
        <Stat
          label="Last change"
          value={historyFailed || !lastChange ? '—' : dayTime(lastChange.effective_at)}
          delta={
            historyFailed
              ? 'not read — the configuration log request failed'
              : lastChange
                ? `${lastChange.actor ?? 'unattributed'} · ${lastChange.setting_key}`
                : 'no versioned change recorded'
          }
        />
        <Stat
          label="Earliest licence expiry"
          value={brandsFailed || !nextLicence ? '—' : day(nextLicence.licence_expires_at)}
          delta={
            brandsFailed
              ? 'not read — the brand request failed'
              : nextLicence
                ? expiringLicences.length > 0
                  ? `${formatCount(expiringLicences.length)} inside the 60-day window`
                  : `${nextLicence.name} · ${daysUntil(nextLicence.licence_expires_at!)} days`
                : 'no licence expiry recorded'
          }
          deltaUp={!brandsFailed && expiringLicences.length > 0}
        />
      </div>

      <div className="halves">
        <Panel title="General settings">
          {/* Reads from two of the three routes, so it waits for both. */}
          {settingsBusy || brandsBusy ? (
            <div className="skeleton" style={{ height: '180px' }}>
              &nbsp;
            </div>
          ) : (
            <div className="stack">
              {generalRows.map((row) => (
                <dl className="def-row" key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </dl>
              ))}
            </div>
          )}
        </Panel>

        {/* The design's four System status indicators (Platform, Player apps,
            Payments, Game providers) have no source on this screen. Rather than
            paint four dots the console cannot verify, the card names the gap. */}
        <Panel title="System status">
          <EmptyState icon="pulse" title="Not sourced on this screen">
            Platform, Player apps, Payments and Game providers are not shown here as four live
            indicators. This screen reads parameters, their history and the brands — none of which
            carries a health signal. Health <em>is</em> measured, and <strong>System admin</strong>{' '}
            is where it is read; four green dots invented here would be a status nobody
            measured.
          </EmptyState>
        </Panel>
      </div>

      <Panel title="System parameters">
        {/*
          WHY THE SCOPE COLUMN SAYS LESS THAN THE DESIGN'S DOES, in the words
          the schema uses — which is why it is here and not in the notice.

          `platform.setting` has no scope column. An override is a second row in
          that table with the same `key` and a non-null `brand_id`, so the only
          two things the data can prove are "there is a brand on this row" and
          "this key is allowed an override at all" (`per_brand`) — and `scopeOf`
          above derives the label from exactly those two.

          The design's third value, "Back office", is a SURFACE rather than a
          brand, and nothing stored distinguishes a parameter the console reads
          from one the player apps read. `session.idle_timeout_min` is the
          parameter where the gap shows, so the notice names it: it governs the
          back office only, and it reads as Global because Global is all the data
          can honestly say. Printing "Back office" would be the screen inventing
          a scope nobody recorded.
        */}
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Global parameters cascade to every brand; only a parameter marked{' '}
            <em>override allowed</em> may be overridden, and an override is kept separately,
            against that one brand. Scope itself is not stored at all — the Scope column is worked
            out from those two things, whether the parameter allows an override and whether a brand{' '}
            {/* The example is session.idle_timeout_min. */}
            is named on it. That is why a &ldquo;Back office&rdquo; scope (a surface, not a brand)
            cannot be shown here: the back office&rsquo;s idle sign-out time governs the back office
            alone and still reads as Global, which is as much as what is stored can honestly
            say. Values print exactly as stored, with no unit or type recorded against them.
            <br />
            Three rows are the exception and their Edit is disabled with the reason on it:{' '}
            <span className="tabular">{RESIDENCY_MODE_KEY}</span> and{' '}
            <span className="tabular">{RESIDENCY_COUNTRIES_KEY}</span> are one decision written as
            two rows, and the platform refuses a change to either on its own. They are set together in{' '}
            <strong>Player residency</strong> below. And{' '}
            the points-per-peso setting is read by nothing — it was
            the points rate until the earning rules replaced it, and the live rate is now a pair of
            whole numbers per brand, set on <strong>Points</strong>. The row stays because it is
            the provenance of those rules, but moving it would change no payment.
          </div>
        </div>
        {settingsBusy ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Platform parameters with their value, scope and date last changed"
            columns={parameterColumnsFor(setEditing, allowed('platform.manage'))}
            rows={visibleSettings}
            rowKey={(s) => `${s.key}:${s.brand ?? 'global'}`}
            onRowClick={(s) => setSelected(s)}
            empty={
              <EmptyState
                icon={settingsFailed ? 'warning-circle' : 'sliders'}
                title={
                  settingsFailed
                    ? 'The parameters could not be read'
                    : 'No parameters in this scope'
                }
              >
                {/* The failed read answers FIRST, and never with a zero. An
                    empty table behind a dismissed error modal is the absence of
                    an answer, not an answer of none — and "this platform has no
                    configured parameters" is a conclusion an operator acts on.

                    BOTH SENTENCES USED TO NAME THEIR SOURCE: the first said
                    `GET /api/platform/settings did not come back`, the second
                    said `platform.setting returned no row`. The names live here
                    now — whoever is debugging a wrong empty state needs to know
                    it is that route and that table, and an operator could do
                    nothing with either. What they need is only WHICH of the two
                    things happened, and both sentences still say that as
                    plainly as they can, because acting on "there are no
                    parameters" when the truth is "nobody could ask" is the
                    expensive mistake here. */}
                {settingsFailed
                  ? 'The request for the parameters did not come back, so this table is empty because nothing was read — not because the platform holds no parameters. The error message that appeared carries what the platform said; try again shortly.'
                  : `No parameter is recorded for ${scopedBrandName ?? 'any brand'}. Nothing was filtered out — there is genuinely nothing stored, and this table is read fresh every time the screen is opened.`}
              </EmptyState>
            }
          />
        )}
      </Panel>

      {/*
        PLAYER RESIDENCY — two of the parameters in the table above, shown as the
        one decision they are.

        A section rather than a module of its own, for the reason its own file
        states: the policy IS platform.setting, it is per brand with a
        platform-wide fallback like every other overridable parameter, it reads
        for platform.view and writes for platform.manage, and its change lands in
        the configuration log immediately below. Read as two untyped rows in the
        table above — "BLOCK_LIST" and "PH" — it is a licence control nobody can
        see the shape of.
      */}
      <ResidencyPolicy />

      <div className="halves">
        <Panel
          title="Configuration log"
          actions={
            <Button small onClick={() => setHistoryOpen(true)} disabled={historyBusy}>
              Full history
            </Button>
          }
        >
          {historyBusy ? (
            <div className="skeleton" style={{ height: '120px' }}>
              &nbsp;
            </div>
          ) : historyFailed ? (
            /* Its own flag, not the parameters': the log is a separate GET and
               can fail while the table above is intact.

               The two sentences below used to name what backs them —
               `GET /api/platform/settings/history` for the failure and
               `platform.setting_history` for the empty read. Recorded here
               instead: an operator cannot call a route or look inside a table,
               and the only thing they must not get wrong is which of the two
               states they are looking at. Both sentences still draw that line
               as hard as they did, because "no parameter has ever been changed
               on this platform" and "the log did not answer" are different
               facts and an auditor acts on the first. */
            <EmptyState icon="warning-circle" title="The configuration log could not be read">
              The request for the configuration log did not come back, so nothing is listed here
              because nothing was read — not because no parameter has ever moved. The error message
              that appeared carries what the platform said; try again shortly.
            </EmptyState>
          ) : visibleHistory.length === 0 ? (
            <EmptyState icon="clock-counter-clockwise" title="No versioned change yet">
              No parameter change is recorded in this scope. Nothing was filtered out — there is
              genuinely nothing stored yet. Every future change is recorded here as the value moves,
              with the person who made it, the value before, the value after, and the time it took
              effect.
            </EmptyState>
          ) : (
            <div className="feed">
              {/* The card shows the most recent few; the modal holds the 50 the
                  API returns. */}
              {visibleHistory.slice(0, 4).map((h) => (
                <div className="feed-row" key={h.id}>
                  <div className="feed-time">{dayTime(h.effective_at)}</div>
                  <div>
                    <span className="text-muted">{h.actor ?? 'unattributed'}</span> ·{' '}
                    <span className="tabular">
                      {h.setting_key} {h.value_before ?? '—'} → {h.value_after}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Brands and licences">
          {/* The header says "scoped to <brand>" and the two panels above obey it.
              This one does not, and says so: GET /api/platform/brands takes no
              brandId, and a licence expiry is a platform obligation rather than a
              per-brand view — hiding the other two brands would hide a renewal. */}
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Every brand on the platform, <strong>not narrowed by the header&rsquo;s brand
              scope</strong> — a licence expiry is a platform-wide obligation, not one brand&rsquo;s.
              The parameters and the configuration log above are scoped to your selection; this
              table and the two brand tiles are not.
            </div>
          </div>
          {brandsBusy ? (
            <div className="skeleton" style={{ height: '180px' }}>
              &nbsp;
            </div>
          ) : (
            <DataTable
              caption="Brands with player counts, licence reference and licence expiry"
              columns={brandColumns}
              rows={brands}
              rowKey={(b) => b.id}
              empty={
                /* `GET /api/platform/brands` and `platform.brand` are named
                   here rather than in the two states below. This is the empty
                   state where the distinction matters most on the whole screen:
                   every brand in this table carries a licence expiry, so a
                   brand that is missing is a renewal nobody is counting down
                   to, and "there are no brands" read off a failed request would
                   quietly retire an obligation to a regulator. Both sentences
                   therefore keep the distinction and lose only the names, which
                   an operator could not act on either way. */
                brandsFailed ? (
                  <EmptyState icon="warning-circle" title="Brands could not be read">
                    The request for the brands did not come back, so no brand is listed because
                    none was read — not because the platform has no brands. A missing brand here is
                    a missing licence renewal, so this must never read as a zero.
                  </EmptyState>
                ) : (
                  <EmptyState icon="buildings" title="No brands">
                    No brand is recorded on the platform. Nothing was filtered out — there is
                    genuinely none yet, and every setting on this screen stays global until a brand
                    exists to override it.
                  </EmptyState>
                )
              }
            />
          )}
        </Panel>
      </div>

      {/* LISTS.configHistory — the full versioned trail. */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Configuration history"
        wide
        actions={
          <Button variant="primary" onClick={() => setHistoryOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Parameter changes are versioned and effective from the stated time; global values
            cascade to every brand unless the parameter is per-brand.
          </div>
        </div>
        <DataTable
          caption="Configuration history: effective time, actor, parameter, before and after values, and scope"
          columns={historyColumns}
          rows={visibleHistory}
          rowKey={(h) => h.id}
          empty={
            /* The same pair as the card behind this dialog, shorter, and the
               same two names behind them: `GET /api/platform/settings/history`
               and `platform.setting_history`. Kept in this comment for the same
               reason, and the two states are still told apart in the words the
               reader gets, because somebody who opened the full history to
               settle a question about a change needs to know whether the answer
               is "it never happened" or "we could not look". */
            historyFailed ? (
              <EmptyState icon="warning-circle" title="The configuration log could not be read">
                The request for the configuration log did not come back. Nothing is listed here
                because nothing was read, not because nothing has been recorded.
              </EmptyState>
            ) : (
              <EmptyState icon="clock-counter-clockwise" title="Nothing versioned yet">
                No parameter change is recorded in this scope. Nothing was filtered out — there is
                genuinely nothing stored yet.
              </EmptyState>
            )
          }
        />
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          This shows the 50 most recent changes. Older versions are in the immutable audit
          trail under Access control.
        </div>
      </Dialog>

      {/* Row click is the read path the disabled Edit cannot be: value,
          description, derived scope and this parameter's own version history. */}
      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.key ?? 'Parameter'}
        subtitle={selected ? scopeOf(selected).label : undefined}
      >
        {selected && (
          <div className="stack">
            <dl className="def-row">
              <dt>Value</dt>
              <dd className="tabular">{selected.value}</dd>
            </dl>
            <dl className="def-row">
              <dt>Per-brand override</dt>
              <dd>{selected.per_brand ? 'Allowed' : 'Not allowed — cascades to every brand'}</dd>
            </dl>
            <dl className="def-row">
              <dt>Applies to</dt>
              <dd>{selected.brand ?? 'All brands'}</dd>
            </dl>
            <dl className="def-row">
              <dt>Last changed</dt>
              <dd className="tabular">{dayTime(selected.updated_at)}</dd>
            </dl>

            <p className="text-muted" style={{ fontSize: '12.5px' }}>
              {selected.description ?? 'No description recorded on this parameter.'}
            </p>

            <div className="panel-title">Versions</div>
            {historyFailed ? (
              <div className="text-subtle" style={{ fontSize: '12.5px' }}>
                The configuration log could not be read, so this parameter&rsquo;s version history
                is unknown — not empty. Nothing below is missing because it does not exist.
              </div>
            ) : visibleHistory.filter((h) => h.setting_key === selected.key).length === 0 ? (
              <div className="text-subtle" style={{ fontSize: '12.5px' }}>
                No versioned change for this parameter in the 50 most recent entries. That means it
                has not moved recently — not that it has never moved.
              </div>
            ) : (
              <div className="feed">
                {visibleHistory
                  .filter((h) => h.setting_key === selected.key)
                  .map((h) => (
                    <div className="feed-row" key={h.id}>
                      <div className="feed-time">{dayTime(h.effective_at)}</div>
                      <div>
                        <span className="text-muted">{h.actor ?? 'unattributed'}</span> ·{' '}
                        <span className="tabular">
                          {h.value_before ?? '—'} → {h.value_after}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            <div className="notice notice-quiet">
              <Icon name="info" />
              {/* There is still no `setting_change_request` table and no
                  pending-change state anywhere in the schema, which is what the
                  sentence below is really reporting. The old wording named the
                  table that does not exist, which is the worst version of this
                  habit: an operator was shown an identifier they could not look
                  up for a thing that was never there. What they need is the
                  consequence — one person can move a parameter on their own —
                  and that is what it says now. */}
              <div>
                This parameter is changed from the Edit on its row, and the change is recorded in
                the log above at the same moment as the value itself; one cannot happen without the
                other. There is no draft, no request to approve and no second approver, so a
                configuration change does <strong>not</strong> go through the two-person
                maker-checker the money paths use: a single holder of{' '}
                <span className="signin-perm">platform.manage</span> moves it, and the log is what
                makes that accountable instead.
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <EditSettingDialog
        setting={editing}
        /* The brands this admin may act on. `sessionBrands` is every brand
           inside their scope, which is exactly the set the route will accept an
           override for — a brand outside it is refused server-side. */
        brands={sessionBrands}
        onClose={() => setEditing(null)}
        onDone={() => setWriteCount((n) => n + 1)}
      />
    </>
  );
}
